import {
  Box,
  Button,
  IconButton,
  InputBase,
  SvgIcon,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Theme, styled } from "@mui/material/styles";
import withStyles from "@mui/styles/withStyles";
import makeStyles from "@mui/styles/makeStyles";
import { ReactComponent as searchIcon } from "assets/icons/icon-search.svg";
import CloseIcon from "@mui/icons-material/Close";
import React, { FormEvent, useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "store";
import { useFocusStyles } from "hooks/focusBorder";
import { useTranslation } from "react-i18next";
import { useBreadCrumbs, usePageTitle } from "hooks";
import { Paginator, SubHeader, theme } from "components";
import { AttendanceStatus } from "utils/graphql/Global";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client/react";
import { GET_ATTENDANCES, UPDATE_ATTENDANCE } from "../../utils/queries";
import { debounce, get, noop, range } from "lodash";
import { mobileDateString } from "../../utils/helpers";
import {
  SessionAttendees,
  SessionAttendees_sessionAttendees,
  SessionAttendees_sessionAttendees_attendees,
} from "../../utils/graphql/SessionAttendees";
import _ from "lodash";
import { EXCEPTIONS_CODES } from "../../utils/constants";
import { AttendanceCreationMethod } from "AttendanceForm/Pages/LearnerDetails/utils";
const SearchIconButton = withStyles((theme: Theme) => ({
  root: {
    "&:hover": {
      backgroundColor: theme.palette.grey["300"],
    },
  },
}))(IconButton);

const style: React.CSSProperties = {
  paddingTop: "16px",
  paddingBottom: "16px",
  borderRadius: "0px",
  marginTop: "20px",
  fontSize: "11px",
  color: "#0091DA",
  borderColor: "#0091DA",
  fontWeight: "bold",
  minWidth: "128px",
};

const backBtnStyle: React.CSSProperties = {
  ...style,
  color: "gray",
  borderColor: "gray",
};

interface ContainerProps {
  background?: boolean;
  mobile?: boolean;
}

const Container = styled(Box, {
  shouldForwardProp: (prop) => prop !== "background" && prop !== "mobile",
})<ContainerProps>(({ theme, background, mobile }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "95%",
  height: background ? "150px" : "auto",
  position: "relative",
  margin: background || mobile ? "0" : "auto",
  zIndex: 1,
  "& > div": {
    background: "transparent",
  },
  "& form": {
    "&:focus-within": {
      border: `2px solid ${theme.palette.common.black}`,
    },
    display: "flex",
    alignItems: "center",
    backgroundColor: background ? "rgba(255, 255, 255, 0.8)" : "#F5F5F5",
    backgroundPosition: "center center",
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
    maxWidth: background ? "1240px" : "auto",
    height: background ? "70px" : "auto",
    borderRadius: 0,
    "& > div": {
      width: "100%",
      "& > div": {
        width: background ? "calc(100% - 65px)" : "calc(100% - 48px)",
        "& input": {
          padding: "0 20px",
          fontSize: background ? "22px" : "16px",
          color: theme.palette.grey["800"],
          position: "relative",
          top: "3px",
          "&::placeholder": {
            opacity: background ? 1 : 0.4,
          },
        },
      },
    },
  },
}));

const HeaderField = styled(Box)(({ theme }) => ({
  marginBottom: "5px",
  fontSize: "12px",
  lineHeight: "16px",
  textAlign: "right",
  "& span": {
    color: theme.palette.grey["800"],
  },
}));
HeaderField.displayName = "HeaderField";
const switchStyles = makeStyles(() => {
  return {
    root: {
      width: "34px",
      height: "20px",
      padding: 0,
      margin: "0 6px",
      overflow: "unset",
    },
    thumb: {
      boxShadow: "none",
      backgroundColor: theme.palette.grey["300"],
      width: "14px",
      height: "14px",
    },
    switchBase: {
      padding: "3px 4px",
      "&$checked": {
        color: theme.palette.grey["100"],
        transform: `translateX(calc(56px - 32px - 12px))`,
        "& + $track": {
          backgroundColor: "transparent",
          opacity: 1,
          border: `1px solid ${theme.palette.grey["200"]}`,
        },
        "& $thumb": {
          backgroundColor: theme.palette.grey["200"],
        },
      },
    },
    track: {
      borderRadius: 40,
      border: `1px solid ${theme.palette.grey["300"]}`,
      backgroundColor: theme.palette.grey["100"],
      opacity: 1,
      boxSizing: "border-box",
    },
    checked: {
      "& $thumb": {
        backgroundColor: theme.palette.grey["100"],
      },
    },
  };
});

const useStyles = makeStyles({
  attendRadio: {
    border: "2px solid #000 !important",
  },
});

export const Attendees = ({ background, onSubmit, mobile = false }: any): React.ReactElement => {
  const dispatch = useAppDispatch();
  const focusClass = useFocusStyles();
  const classes = useStyles();
  const initialSearchTerm = useSelector((state: RootState) => state.core.searchText);
  const [searchText, setSearchText] = useState(initialSearchTerm);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const customSwitch = switchStyles();
  const [totalCount, setTotalCount] = useState<number>(0);
  const [pages, setPages] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [skipCount, setSkipCount] = useState<number>(0);

  const [attendeesData, setAttendeesData] = useState<SessionAttendees_sessionAttendees | null>(
    null,
  );
  const [attendees, setAttendees] = useState<
    (SessionAttendees_sessionAttendees_attendees | null)[] | null
  >([]);

  const [allAttended, setAllAttended] = useState(false);

  const { handleBreadCrumb, handleBreadCrumbNavigation, breadCrumbData } = useBreadCrumbs();

  useEffect(() => {
    if (breadCrumbData) {
      handleBreadCrumb(
        { title: "Attendees", path: location.pathname, type: "Attendees" },
        "Attendees",
        "",
      );
    }
  }, []);

  const handleBreadCrumbClick = (path: string, breadCrumbKey: any) => {
    handleBreadCrumbNavigation(path, breadCrumbKey);
  };

  const sessionId = useMemo(() => {
    return get(params, "sessionId", "-1");
  }, [params]);

  const { loading, data, error, refetch } = useQuery<SessionAttendees>(GET_ATTENDANCES, {
    fetchPolicy: "no-cache",
    variables: {
      sessionId: sessionId,
    },
  });

  // Handle GraphQL errors
  useEffect(() => {
    if (error && "graphQLErrors" in error) {
      const graphQLErrors = (error as any).graphQLErrors;
      if (
        graphQLErrors?.find((err: any) =>
          Object.getOwnPropertyNames(err?.extensions)?.find(
            (p: string) => p.indexOf(EXCEPTIONS_CODES.UnAuthorized) != -1,
          ),
        )
      ) {
        navigate("/unauthorized");
      }
    }
  }, [error, navigate]);

  useEffect(() => {
    if (data) {
      if (data.sessionAttendees) {
        setAttendeesData(data.sessionAttendees as SessionAttendees_sessionAttendees);

        pagingAttendees(
          data.sessionAttendees?.attendees as SessionAttendees_sessionAttendees_attendees[],
        );

        const totalCount = data.sessionAttendees?.attendees?.length || 0;

        setTotalCount(totalCount);

        setAllAttended(
          (totalCount > 0 &&
            data?.sessionAttendees?.attendees?.every(
              (a) => a?.attendanceStatus == AttendanceStatus.ATTENDED,
            )) ||
            false,
        );
      }
    }
  }, [data]);

  useEffect(() => {
    setSkipCount(Number(process.env.REACT_APP_ATTENDEES_COUNT) * (currentPage - 1));
  }, [attendeesData, currentPage]);

  useEffect(() => {
    pagingAttendees(attendeesData?.attendees);
  }, [skipCount]);

  useEffect(() => {
    setCurrentPage(1);
    pagingAttendees(attendees);
  }, [searchText]);

  const pagingAttendees = (
    attendeesList: (SessionAttendees_sessionAttendees_attendees | null)[] | null | undefined,
  ) => {
    if (attendeesList) {
      const attendeesPage = _.chain(attendeesList)
        .drop(skipCount)
        .take(Number(process.env.REACT_APP_ATTENDEES_COUNT))
        .value();

      setAttendees(attendeesPage);

      const count = attendeesList?.length || 0;

      const totalPages = Math.ceil(count / Number(process.env.REACT_APP_ATTENDEES_COUNT));
      setPages(range(1, totalPages + 1));
    } else {
      setAttendees([]);
      setPages([]);
    }
  };

  useEffect(() => {
    dispatch({
      type: "loader/showandhide",
      payload: { show: loading, message: "Loading attendees" },
    });
  }, [loading, dispatch]);

  const updateSearchText = (value: string) => {
    setSearchText(value);

    if (value && value.trim() != "") {
      setAttendees(
        (attendeesData?.attendees || []).filter((attendaee) => {
          return (
            `${attendaee?.firstName} ${attendaee?.lastName}`
              ?.toLowerCase()
              ?.includes(value.toLowerCase()) ||
            attendaee?.email?.toLowerCase()?.includes(value.toLowerCase())
          );
        }),
      );
    } else {
      setAttendees(attendeesData?.attendees || []);
    }
  };

  const changeAllAttended = (event: any) => {
    const checked = event.target.checked;

    setAllAttended(checked);

    if (checked === true) {
      attendeesData?.attendees?.forEach((attendee) => {
        if (attendee != null) attendee.attendanceStatus = AttendanceStatus.ATTENDED;
        return attendee;
      });
    } else {
      attendeesData?.attendees?.forEach((attendee) => {
        if (attendee != null) attendee.attendanceStatus = AttendanceStatus.DID_NOT_ATTEND;
        return attendee;
      });
    }

    setAttendees([...(attendees || [])]);
  };

  const changeAttended = (
    event: any,
    attendee: SessionAttendees_sessionAttendees_attendees | null,
  ) => {
    if (event.target.checked === true) {
      if (attendee != null) attendee.attendanceStatus = AttendanceStatus.ATTENDED;
    } else {
      setAllAttended(false);
      if (attendee != null) attendee.attendanceStatus = AttendanceStatus.DID_NOT_ATTEND;
    }

    setAllAttended(
      attendeesData?.attendees?.every((a) => a?.attendanceStatus == AttendanceStatus.ATTENDED) ||
        false,
    );

    setAttendees([...(attendees || [])]);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    handleSaveAttendance();
  };

  const [updateAttendance] = useMutation(UPDATE_ATTENDANCE);

  const handleSaveAttendance = () => {
    dispatch({ type: "loader/showandhide", payload: { show: true } });
    const postData = {
      sessionId: sessionId,
      activityId: attendeesData?.activityId,
      activityType: attendeesData?.activityType,
      externalSessionId: attendeesData?.externalSessionId,
      programmeId: attendeesData?.programmeId,
      updatedAttendanceByLearnerId: attendeesData?.attendees?.map((attendee) => {
        return {
          key: attendee?.learnerId,
          value: attendee?.attendanceStatus,
        };
      }),
      deletedLearnerIds: [],
      attendanceCreationMethod: AttendanceCreationMethod.ADMIN,
    };
    updateAttendance({ variables: { model: postData } })
      .then(() => {
        dispatch({ type: "loader/showandhide", payload: { show: false } });
        navigate(`/submit/${sessionId}`);
      })
      .catch(() => {
        dispatch({ type: "loader/showandhide", payload: { show: false } });
        dispatch({
          type: "alert/show",
          payload: {
            type: "error",
            title: "Attendance Update",
            message: "Attendance Update Failed",
          },
        });
      });
  };

  const getBrowserTitleText = () => {
    const parameterId = get(params, "sessionId", "-1");
    return `${t("SESSION ATTENDANCE CONFIRMATION")} - ${parameterId}`;
  };

  usePageTitle(getBrowserTitleText(), true);

  return (
    <>
      <Box display={{ xs: "none", sm: "block" }}>
        <SubHeader
          contentType="Attendance"
          breadCrumbData={breadCrumbData}
          handleBreadCrumbClick={handleBreadCrumbClick}
          noDefaultTitle={true}
        />
      </Box>
      <Box paddingTop={{ xs: "30px", sm: "0" }}>
        <Container background={!!background} mobile={mobile}>
          <Box style={{ width: mobile ? undefined : "1100px" }}>
            <Box style={{ display: "flex", justifyContent: "space-between" }}>
              <Box style={{ width: "48%" }}>
                <Box style={{ fontSize: "22px", fontWeight: 400 }} paddingBottom="15px">
                  {t("2_confirm_attendance")}
                </Box>
                <form noValidate onSubmit={handleSubmit} aria-label={t("Attendee_Search_Bar")}>
                  <Box boxShadow={background ? 0 : 0}>
                    <InputBase
                      inputProps={{
                        placeholder: t("search_attendee"),
                        "aria-label": t("Press_Escape_key_To_Clear"),
                      }}
                      value={searchText}
                      onChange={(event) => updateSearchText(event.target.value)}
                      type="search"
                      className={focusClass.focusItem}
                    />
                    {mobile && searchText ? (
                      <IconButton
                        type="button"
                        className={focusClass.focusItem}
                        aria-label={t("aria_label_clear_search")}
                        onClick={(e) => {
                          e.preventDefault();
                          updateSearchText("");
                        }}
                        size="large"
                      >
                        <CloseIcon fontSize={background ? "large" : "inherit"} />
                      </IconButton>
                    ) : (
                      <SearchIconButton
                        onClick={(e) => {
                          e.preventDefault();
                          updateSearchText(searchText);
                        }}
                        type="submit"
                        aria-label={t("search").toLowerCase()}
                        style={{ height: "48px", width: "48px" }}
                        className={focusClass.focusItem}
                      >
                        <SvgIcon
                          component={searchIcon}
                          style={{
                            position: "absolute",
                            margin: "16px 6px 8px 10px",
                            fontSize: background ? "30px" : "22px",
                          }}
                        />
                      </SearchIconButton>
                    )}
                  </Box>
                </form>
              </Box>
              <Box paddingTop={{ xs: "30px", sm: "0" }}>
                <HeaderField>
                  <span style={{ fontWeight: "bold" }}>{attendeesData?.programmeName}</span>
                </HeaderField>
                <HeaderField>
                  <span>{attendeesData?.activityName}</span>
                </HeaderField>
                <HeaderField>
                  <span>
                    {attendeesData?.sessionName}: {attendeesData?.externalSessionId}
                  </span>
                </HeaderField>
                <HeaderField>
                  <span>
                    {" "}
                    {attendeesData?.startDate
                      ? `${mobileDateString(new Date(attendeesData?.startDate))} ${new Date(attendeesData?.startDate).toLocaleTimeString()}`
                      : "-"}
                  </span>
                </HeaderField>
                <HeaderField>
                  <span>{attendeesData?.location}</span>
                </HeaderField>
              </Box>
            </Box>
            <Box margin="30px 0px">
              <Box marginBottom={{ xs: "10px", sm: "20px" }} style={{ display: "flex" }}>
                <TableContainer>
                  <Table aria-label={t("Attendees_table")}>
                    <TableHead>
                      <TableRow>
                        {mobile ? (
                          <>
                            <TableCell align="left" style={{ fontWeight: 700 }}>
                              {t("attendee")}
                            </TableCell>
                            <TableCell>{t("email")}</TableCell>
                            <TableCell></TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell align="left" style={{ fontWeight: 700 }} scope="col">
                              {t("attendee")}
                            </TableCell>
                            <TableCell align="center" scope="col">
                              <Box
                                style={{
                                  fontWeight: 400,
                                  fontSize: "16px",
                                  color: theme.palette.grey["200"],
                                  display: "inline-block",
                                }}
                                paddingRight="10px"
                              >
                                {attendees?.length}
                              </Box>
                              {t("total")}
                            </TableCell>
                            <TableCell align="right" style={{ fontWeight: 700 }} scope="col">
                              {t("attended")}{" "}
                              <Switch
                                focusVisibleClassName={classes.attendRadio}
                                slotProps={{
                                  input: { "aria-label": t("Mark_As_All_Attended") },
                                }}
                                color="primary"
                                name="switch"
                                checked={allAttended}
                                classes={customSwitch}
                                onClick={changeAllAttended}
                                disabled={totalCount == 0}
                              />
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {attendees?.length ? (
                        mobile ? (
                          attendees?.map((row, index) => {
                            return (
                              <React.Fragment key={"mobile_" + index}>
                                <TableRow>
                                  <TableCell align="left">
                                    <Box>{row?.firstName + " " + row?.lastName}</Box>
                                    <Box>{row?.email}</Box>
                                  </TableCell>
                                </TableRow>
                              </React.Fragment>
                            );
                          })
                        ) : (
                          attendees?.map((row, index) => {
                            return (
                              <React.Fragment key={index}>
                                {row && (
                                  <TableRow key={index}>
                                    <TableCell align="left">
                                      {row?.firstName + " " + row?.lastName}
                                    </TableCell>
                                    <TableCell align="left"></TableCell>
                                    <TableCell align="right">
                                      <Switch
                                        focusVisibleClassName={classes.attendRadio}
                                        tabIndex={0}
                                        slotProps={{
                                          input: {
                                            "aria-label": row?.firstName + " " + row?.lastName,
                                          },
                                        }}
                                        color="primary"
                                        name="switch"
                                        classes={customSwitch}
                                        checked={row?.attendanceStatus == AttendanceStatus.ATTENDED}
                                        onChange={(e) => changeAttended(e, row)}
                                      />
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })
                        )
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3} style={{ textAlign: "center" }}>
                            {t("no_users")}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              <Box>
                <Paginator
                  pages={pages}
                  currentPage={currentPage}
                  isMobile={mobile}
                  onChange={setCurrentPage}
                />
              </Box>
              <Box display={"flex"} justifyContent="space-between">
                <Button
                  variant="outlined"
                  onClick={() => navigate("/attendance")}
                  className={focusClass.focusItem}
                  role="link"
                  style={backBtnStyle}
                >
                  {t("back_sessions")}
                </Button>
                <Button
                  variant="outlined"
                  className={focusClass.focusItem}
                  role="link"
                  onClick={() => handleSaveAttendance()}
                  style={style}
                  disabled={totalCount == 0}
                >
                  {t("submit")}
                </Button>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};
